import * as React from 'react';
import { Button, ButtonTypes } from 'common/components/core/button';
import { Volume, VolumeUnitType } from 'common/models/internal/Volume';
import { EnhancedField } from 'common/components/core/form/Field';
import { HelpTooltip } from 'common/components/core/help';
import { VolumeInput } from 'common/entity/components/volume/VolumeInput';
import { LineItemPart } from 'common/models/internal/LineItemPart';
import { Section } from 'common/entity/generic/SingleEntityEditView';
import { themr } from 'react-css-themr';

import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';
import classnames from 'classnames';
import defaultTheme from './LineItemPartsInput.scss';
import {
    Dimensions,
    preferredUnitsForDistance,
    sourceUnitForDistance
} from 'common/models/internal/Dimensions';
import { Units, UnitSystemsEnum } from 'common/enums/UnitEnum';

class LineItemPartsInput extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            parts: props?.parts,
            shouldConvertToPreferredUnit: true
        };
    }

    componentDidMount() {
        const { shouldConvertToPreferredUnit } = this.state;
        shouldConvertToPreferredUnit &&
            this.setState({ parts: this.getUpdatedPartsByUnit() });
    }

    getUpdatedPartsByUnit = () => {
        const { parts, unitConverter } = this.props;

        const updatedParts = parts.map(part => {
            return part.convertToPreferredUnit(unitConverter);
        });
        return updatedParts;
    };

    handlePartDelete = event => {
        const { id } = event.target;
        this.onChange(parts => parts.delete(id));
    };

    handleVolumeChange = (index, volume) => {
        this.onChange(parts => {
            const part = parts.get(index);
            const updatedPart = part.setVolume(volume);
            return parts.set(index, updatedPart);
        });
    };

    handleDimensionsChange = (index, dimensions) => {
        const { unitConverter } = this.props;
        this.onChange(parts => {
            const part = parts.get(index);
            let updatedPart = part;
            if (!dimensions.hasValidationErrors() && dimensions.isFilled()) {
                let { volume: updatedVolume } = part;
                const preferredUnitForVolume = updatedVolume.getPreferredUnit(
                    unitConverter
                );
                if (
                    unitConverter.getPreferredSystem({
                        unitType: preferredUnitForVolume.measure
                    }) === UnitSystemsEnum.Imperial
                ) {
                    const volumeInImperialGallon = unitConverter.basicConvert({
                        value: NumberUtils.toNumber(
                            dimensions.getTotalVolume()
                        ),
                        sourceUnit: Units.list.volume.CubicInch,
                        targetUnit: updatedVolume.getPreferredUnit(
                            unitConverter
                        )
                    });
                    updatedVolume = updatedVolume.setValue(
                        volumeInImperialGallon
                    );
                } else {
                    updatedVolume = updatedVolume.setValue(
                        dimensions.getTotalVolume()
                    );
                }

                updatedPart = part.setVolume(updatedVolume);
            }
            updatedPart = updatedPart.setDimensions(dimensions);
            return parts.set(index, updatedPart);
        });
    };

    handleWeightChange = (weight, event) => {
        const { id } = event.target;
        this.onChange(parts => {
            const part = parts.get(id);
            const updatedPart = part.setWeight(weight);
            return parts.set(id, updatedPart);
        });
    };

    onPartAddClick = () => {
        this.onChange(parts => {
            return parts.push(
                LineItemPart.fromJS({
                    volume: {
                        unit: VolumeUnitType.MetersCubic,
                        value: null
                    },
                    weight: null,
                    dimensions: {
                        length: null,
                        breadth: null,
                        height: null
                    }
                })
            );
        });
    };

    onChange = callback => {
        const { onChange, parts } = this.props;

        const updatedParts = callback(parts);
        this.setState(
            { parts: updatedParts, shouldConvertToPreferredUnit: false },
            () => {
                onChange && onChange(updatedParts);
            }
        );
    };

    render() {
        const {
            theme,
            unitConverter,
            translationsObject,
            t,
            parts,
            weightTestId,
            volumeTestId
        } = this.props;

        const partComponents = parts.toArray().map((part, index) => {
            const { weight, volume, dimensions } = part;
            let weightError = null;
            if (
                !DataUtils.isEmpty(weight) &&
                !NumberUtils.isPositiveNumber(weight)
            ) {
                weightError = t(translationsObject.weightValidationError);
            }
            const weightPreferredUnitText = part.getPreferredUnitTextForWeight(
                unitConverter
            );

            const distanceUnit = unitConverter.getPreferredUnit({
                preferredUnits: preferredUnitsForDistance,
                sourceUnit: sourceUnitForDistance
            });
            return (
                <Section key={index}>
                    <div className={theme.skuProperties}>
                        {index !== 0 ? (
                            <Button
                                id={index}
                                buttonType={ButtonTypes.Icon}
                                circle
                                icon="delete-bin"
                                className={classnames(
                                    theme.btn,
                                    theme.deleteBtn
                                )}
                                theme={theme}
                                onClick={this.handlePartDelete}
                            />
                        ) : (
                            []
                        )}
                        <span className={theme.title}>
                            {t(translationsObject.rowLabel, {
                                count: index + 1
                            })}
                        </span>
                        <div className={theme.fieldGroup}>
                            <VolumeInput
                                testId={volumeTestId}
                                translationsObject={translationsObject}
                                theme={theme}
                                id={index}
                                deletable={false}
                                volume={
                                    volume ||
                                    Volume.fromJS({
                                        unit: VolumeUnitType.MetersCubic,
                                        value: null
                                    })
                                }
                                dimensions={
                                    dimensions ||
                                    Dimensions.fromJS({
                                        length: null,
                                        breadth: null,
                                        height: null
                                    })
                                }
                                onChange={this.handleVolumeChange}
                                onDimensionsChange={this.handleDimensionsChange}
                                showVolumeUnit={true}
                                distanceUnit={distanceUnit}
                            />
                        </div>
                        <div
                            className={classnames(
                                theme.fieldGroup,
                                theme.halfWidth
                            )}
                        >
                            <span
                                className={classnames(
                                    theme.label,
                                    theme.subLabel
                                )}
                            >
                                {t(translationsObject.weightLabel)}
                            </span>
                            <EnhancedField
                                testId={weightTestId}
                                id={index}
                                type="text"
                                element="input"
                                value={weight}
                                rightIcon={weightPreferredUnitText}
                                error={weightError}
                                onChange={this.handleWeightChange}
                                theme={theme}
                            />
                        </div>
                    </div>
                </Section>
            );
        });
        return (
            <>
                {partComponents}
                <div className={theme.row}>
                    <span
                        className={theme.addText}
                        onClick={this.onPartAddClick}
                    >
                        {partComponents.length
                            ? `+ ${t(translationsObject.addAnotherPart)}`
                            : `+ ${t(translationsObject.addPart)}`}
                    </span>
                    {partComponents.length ? (
                        <HelpTooltip
                            tooltipText={t(translationsObject.addPartTooltip)}
                        />
                    ) : null}
                </div>
            </>
        );
    }
}

export default themr('LineItemPartsInput', defaultTheme)(LineItemPartsInput);
